/**
 * Prüfungen für die Formulare der Finanzen, mit Fehlertext am Feld.
 *
 * Alle Formulare tragen `noValidate`: `Field` aus @elbklar/ui reicht
 * `required` an das Element durch, ohne `noValidate` hielte der Browser das
 * Absenden mit seiner Blase auf, bevor unser Fehlertext erscheint (wie in
 * crm-web). Damit fielen die Prüfungen des Browsers weg — `required`,
 * `min`/`max` an Zahlenfeldern, `type="url"`, leere Datumsfelder. Sie stehen
 * jetzt hier, die Grenzen aus den Pydantic-Schemas der finance-api.
 *
 * Jede Prüfung gibt den Fehlertext oder `undefined`. Beträge prüft
 * `betragFehler` aus `lib/betrag.ts`.
 */

/** Fehler je Feld; ein fehlender Eintrag heißt: in Ordnung. */
export type Fehler<K extends string = string> = Partial<Record<K, string | undefined>>;

/** Steht irgendwo ein Fehler? */
export function hatFehler(fehler: Fehler): boolean {
  return Object.values(fehler).some(Boolean);
}

/** Der Fehler eines Felds ist erledigt, sobald dort getippt wird. */
export function ohne<F extends Fehler>(fehler: F, feld: keyof F & string): F {
  if (!fehler[feld]) return fehler;
  const rest: F = { ...fehler };
  delete (rest as Fehler)[feld];
  return rest;
}

/** Leer oder nur Leerzeichen: `fehlt` (z. B. „Name fehlt“). */
export function pflicht(wert: string | null | undefined, fehlt: string): string | undefined {
  return (wert ?? '').trim() ? undefined : fehlt;
}

/** Höchstlänge wie `max_length` im Schema, gezählt ohne Leerzeichen am Rand. */
export function hoechstens(wert: string, max: number): string | undefined {
  return wert.trim().length > max ? `Höchstens ${max} Zeichen` : undefined;
}

function bereich(min?: number, max?: number): string {
  if (min !== undefined && max !== undefined) return `Ganze Zahl von ${min} bis ${max}`;
  if (min !== undefined) return `Ganze Zahl ab ${min}`;
  if (max !== undefined) return `Ganze Zahl bis ${max}`;
  return 'Ganze Zahl';
}

/**
 * Ganze Zahl in Grenzen, etwa „Tag im Monat“ 1–28.
 *
 * Leer ist ein Fehler nur mit `pflicht`; der Text nennt dann wie bei jeder
 * anderen falschen Eingabe den Bereich. Ein `<input type="number">` meldet
 * eine angefangene Eingabe („-“, „1e“) als leer: „… fehlt“ unter einer
 * sichtbaren Eingabe wäre irreführend. `unlesbar` (siehe `unlesbareFelder`)
 * ist so eine angefangene Eingabe und gilt nie als leer.
 */
export function ganzzahl(
  wert: string,
  { min, max, pflicht = false, unlesbar = false }: { min?: number; max?: number; pflicht?: boolean; unlesbar?: boolean } = {}
): string | undefined {
  const text = bereich(min, max);
  if (unlesbar) return text;
  const t = wert.trim();
  if (!t) return pflicht ? text : undefined;
  if (!/^-?\d+$/.test(t)) return text;
  const zahl = Number(t);
  if (min !== undefined && zahl < min) return text;
  if (max !== undefined && zahl > max) return text;
  return undefined;
}

const ADRESSE_OHNE_SCHEMA = 'Adresse mit https:// beginnen';
const ADRESSE_UNVOLLSTAENDIG = 'Vollständige Adresse eingeben, z. B. https://www.sab.de';

/**
 * Web-Adresse mit Schema und Host; leer ist erlaubt.
 *
 * Ohne `https://` speicherte die API „www.sab.de/…“ (sie prüft nur die Länge):
 * „Quelle öffnen“ wurde ein relativer Link, der Wochenlauf konnte die Quelle
 * nicht lesen, die Dublettenerkennung griff nicht.
 */
export function httpsAdresse(wert: string, { max }: { max?: number } = {}): string | undefined {
  const t = wert.trim();
  if (!t) return undefined;
  if (!/^https?:\/\//i.test(t)) return ADRESSE_OHNE_SCHEMA;
  try {
    const url = new URL(t);
    if (!url.hostname.includes('.') || url.hostname.startsWith('.') || url.hostname.endsWith('.')) {
      return ADRESSE_UNVOLLSTAENDIG;
    }
  } catch {
    return ADRESSE_UNVOLLSTAENDIG;
  }
  return max !== undefined ? hoechstens(t, max) : undefined;
}

/**
 * Nur `http(s)://`-Adressen werden ein Link, alles andere bleibt Text.
 *
 * Adressen aus Radar-Daten (Quelle, Referenz, Suche) kommen auch vom Roboter
 * und aus älteren Einträgen; ein `javascript:` darf nie in `href` landen.
 */
export function alsLink(url: string | null | undefined): string | undefined {
  const t = (url ?? '').trim();
  if (!/^https?:\/\//i.test(t)) return undefined;
  try {
    const geprueft = new URL(t);
    if (geprueft.protocol !== 'http:' && geprueft.protocol !== 'https:') return undefined;
    if (!geprueft.hostname) return undefined;
  } catch {
    return undefined;
  }
  return t;
}

/**
 * Datum als `YYYY-MM-DD`, wie `<input type="date">` es liefert.
 *
 * Ein halb ausgefülltes Datumsfeld meldet der Browser als leer; `unlesbar`
 * (siehe `unlesbareFelder`) unterscheidet das von einem leeren Feld.
 */
export function datum(
  wert: string,
  { leerErlaubt = false, unlesbar = false }: { leerErlaubt?: boolean; unlesbar?: boolean } = {}
): string | undefined {
  if (unlesbar) return 'Datum vollständig eingeben';
  const t = wert.trim();
  if (!t) return leerErlaubt ? undefined : 'Datum fehlt';
  const teile = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (!teile) return 'Datum vollständig eingeben';
  const [jahr, monat, tag] = teile.slice(1).map(Number);
  const geprueft = new Date(Date.UTC(jahr, monat - 1, tag));
  if (geprueft.getUTCFullYear() !== jahr || geprueft.getUTCMonth() !== monat - 1 || geprueft.getUTCDate() !== tag) {
    return 'Datum vollständig eingeben';
  }
  return undefined;
}

/**
 * Ein späteres Datum darf nicht vor dem früheren liegen (Ende vor Beginn).
 * Fehlt eines der beiden, gibt es nichts zu vergleichen.
 */
export function nichtVor(spaeter: string, frueher: string, text: string): string | undefined {
  if (!spaeter || !frueher || datum(spaeter) || datum(frueher)) return undefined;
  return spaeter < frueher ? text : undefined;
}

/**
 * Namen der Felder mit angefangener, unlesbarer Eingabe.
 *
 * `<input type="number">` und `type="date"` geben dem Formular für „-“ oder
 * ein halbes Datum `''`, ohne `noValidate` hielt der Browser das auf
 * (`validity.badInput`). Die Felder brauchen dafür ein `name`.
 */
export function unlesbareFelder(form: Pick<HTMLFormElement, 'elements'>): Set<string> {
  const namen = new Set<string>();
  for (const element of Array.from(form.elements)) {
    const feld = element as Partial<HTMLInputElement>;
    if (feld.name && feld.validity?.badInput) namen.add(feld.name);
  }
  return namen;
}

/** Nach dem Absenden zum ersten Feld mit Fehler, damit er nicht übersehen wird. */
export function zumErstenFehler(form: HTMLFormElement | null): void {
  if (!form) return;
  requestAnimationFrame(() => form.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus());
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Ist `wert` eine UUID? Für IDs aus der Adresse (`/radar/:id`), bevor sie in
 * einen API-Pfad gehen: `useParams` dekodiert `..%2F..%2Floans%2F<id>` zu
 * `../../loans/<id>`, und axios würde damit `/api/loans/<id>` ansprechen
 * (Gegenprüfung D2, Sicherheit).
 */
export function istUuid(wert: string | null | undefined): wert is string {
  return typeof wert === 'string' && UUID.test(wert);
}
